var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Handles picture upload for a glass damage declaration and validates enough photos have been provided, per a trigram-specific required count setting.
 * touches: glass breakage damage declaration, damage picture upload, required photos validation.
 * invariants: requiredPhotos falls back to 0 if the trigram-specific setting is missing (checks a per-trigram key then a grouped fallback); hasEnoughPhotos is always true when requiredPhotos is 0; photos-valid re-emits on any relevant change.
 * inputs: changeKey/validateRequested props, glassDamage.pictureCollection state, damageRequiredPhotosCount settings, typeTrigram from qualification.
 * outputs: dispatches updateGlassDamage to update pictureCollection, emits update:photos-valid, displays a required-photos error when validateRequested and photos are missing.
 * see: PictureCollectionUpload.vue, createDamageStore, settings store
 */
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { UiCard } from '@claims-ia/design-system';
import PictureCollectionUpload from '@declaration/components/upload/PictureCollectionUpload.vue';
import { GLASS_DAMAGE_GLASS_TYPE_PLAIN_CHOICES, GLASS_DAMAGE_GLASS_TYPE_IMAGE_CHOICES, } from '@declaration/constants/glassDamageConstants';
import { ClaimFileType } from '@shared/types/file';
const glassDamage = namespace('glassDamage');
const settings = namespace('settings');
const qualification = namespace('qualification');
let GlassTypeComponent = class GlassTypeComponent extends Vue {
    constructor() {
        super(...arguments);
        this.fileType = ClaimFileType.GlassDamagePicture;
    }
    get change() {
        return this.changeGetter(this.changeKey);
    }
    get validation() {
        return this.validationGetter(this.changeKey);
    }
    get glassDamage() {
        return this.change.data;
    }
    get glassTypeImageChoices() {
        return GLASS_DAMAGE_GLASS_TYPE_IMAGE_CHOICES;
    }
    get glassTypeButtonChoices() {
        return GLASS_DAMAGE_GLASS_TYPE_PLAIN_CHOICES;
    }
    get pictureCollection() {
        return this.glassDamage.pictureCollection;
    }
    get requiredPhotos() {
        if (!this.trigram || !this.setting)
            return 0;
        let value = null;
        try {
            value = this.setting(`damageRequiredPhotosCount${this.trigram}`);
        }
        catch (_a) { }
        if (value === null || value === undefined) {
            try {
                const grouped = this.setting('damageRequiredPhotosCount');
                if (grouped && typeof grouped === 'object')
                    value = grouped[this.trigram];
            }
            catch (_b) { }
        }
        const count = Number(value);
        return isNaN(count) ? 0 : count;
    }
    get hasEnoughPhotos() {
        var _a, _b;
        if (this.requiredPhotos === 0)
            return true;
        const collection = this.pictureCollection;
        if (!collection || typeof collection === 'string')
            return false;
        return ((_b = (_a = collection.uploadedAppFiles) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) >= this.requiredPhotos;
    }
    onPictureCollectionUpdate(pictureCollection) {
        this.updateGlassDamage({
            changeKey: this.changeKey,
            data: { pictureCollection },
        });
    }
    mounted() {
        this.$emit('update:photos-valid', this.hasEnoughPhotos);
    }
    onHasEnoughPhotosChange(newVal) {
        this.$emit('update:photos-valid', newVal);
    }
    onPhotosValidUpdate(val) {
        this.$emit('update:photos-valid', val);
    }
};
__decorate([
    glassDamage.Getter('change')
], GlassTypeComponent.prototype, "changeGetter", void 0);
__decorate([
    glassDamage.Getter('validation')
], GlassTypeComponent.prototype, "validationGetter", void 0);
__decorate([
    glassDamage.Action('updateDamage')
], GlassTypeComponent.prototype, "updateGlassDamage", void 0);
__decorate([
    settings.Getter('setting')
], GlassTypeComponent.prototype, "setting", void 0);
__decorate([
    qualification.Getter('typeTrigram')
], GlassTypeComponent.prototype, "trigram", void 0);
__decorate([
    Watch('hasEnoughPhotos')
], GlassTypeComponent.prototype, "onHasEnoughPhotosChange", null);
GlassTypeComponent = __decorate([
    Component({
        components: {
            PictureCollectionUpload,
            UiCard,
        },
        props: {
            changeKey: { type: [Number, String], default: null },
            validateRequested: { type: Boolean, default: false },
        },
    })
], GlassTypeComponent);
export default GlassTypeComponent;
